// Self-issued identity for the browser this clock-in kiosk runs in.
//
// This is NOT a hardware identifier - a browser (even in Edge kiosk mode)
// has no API for MAC address, IMEI, or serial number, by design. Instead we
// mint a random id ourselves on first load and persist it, so a tenant-admin
// can bind THIS id to a facility once via the register-device screen. Every
// later clock-in from the same kiosk sends the same id back, letting the
// backend skip the GPS proximity check for devices it already trusts.
//
// Persistence is intentionally redundant (localStorage + a long-lived
// cookie) because we cannot guarantee against a kiosk profile reset or a
// "clear on exit" policy wiping one storage area but not the other. If both
// are ever wiped, the kiosk simply shows up as "unregistered" again and
// needs a ~30 second re-registration - that graceful fallback is the real
// answer to persistence risk, not a claim that this id is unlosable.

const STORAGE_KEY = "clockin_device_id";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 10; // 10 years

const generateUuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  // Fallback for browsers without crypto.randomUUID (older Edge/WebViews)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const readCookie = (name) => {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value) => {
  try {
    document.cookie = `${name}=${encodeURIComponent(
      value
    )};max-age=${COOKIE_MAX_AGE_SECONDS};path=/;SameSite=Lax`;
  } catch (err) {
    console.warn("Unable to persist device id cookie:", err);
  }
};

export const getOrCreateDeviceId = () => {
  let deviceId = null;

  try {
    deviceId = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    console.warn("localStorage unavailable for device id:", err);
  }

  if (!deviceId) {
    deviceId = readCookie(STORAGE_KEY);
  }

  if (!deviceId) {
    deviceId = generateUuid();
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, deviceId);
  } catch (err) {
    console.warn("Unable to persist device id to localStorage:", err);
  }
  writeCookie(STORAGE_KEY, deviceId);

  // Best-effort request to keep this origin's storage out of automatic
  // eviction under disk pressure. Not supported everywhere, and does not
  // protect against an explicit "clear on exit" policy - see file header.
  try {
    navigator.storage?.persist?.();
  } catch (err) {
    // no-op - persistence is a nice-to-have, not a requirement
  }

  return deviceId;
};
